import apiConfig from "../ApiConfig";

// Create a new employee incident report
export const createEmployeeIncidentReportApi = async (data) => {
    try {
        const response = await apiConfig.post(`/api/incident-reporting/employee-reports/`, data);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Get employee incident reports (filtered by reporter email)
export const getEmployeeIncidentReportsApi = async (reporter) => {
    try {
        const url = reporter
            ? `/api/incident-reporting/employee-reports/?reporter=${encodeURIComponent(reporter)}`
            : `/api/incident-reporting/employee-reports/`;
        const response = await apiConfig.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Get all incident reports for a company (filtered by customer_id with pagination and filters)
export const getCompanyIncidentReportsApi = async (customerId, params = {}) => {
    try {
        // Build query parameters
        const queryParams = new URLSearchParams();
        
        if (customerId) {
            queryParams.append('customer_id', customerId);
        }
        
        // Add pagination parameters
        if (params.page) queryParams.append('page', params.page);
        if (params.page_size) queryParams.append('page_size', params.page_size);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.skip) queryParams.append('skip', params.skip);
        
        // Add filter parameters
        if (params.status) queryParams.append('status', params.status);
        if (params.severity) queryParams.append('severity', params.severity);
        if (params.category) queryParams.append('category', params.category);
        if (params.incident_type) queryParams.append('incident_type', params.incident_type);
        if (params.search) queryParams.append('search', params.search);
        if (params.incident_no) queryParams.append('incident_no', params.incident_no);
        if (params.reporter) queryParams.append('reporter', params.reporter);
        if (params.employee_id) queryParams.append('employee_id', params.employee_id);
        
        const url = `/api/incident-reporting/employee-reports/?${queryParams.toString()}`;
        const response = await apiConfig.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Get a single employee incident report by ID
export const getEmployeeIncidentReportByIdApi = async (reportId) => {
    try {
        const response = await apiConfig.get(`/api/incident-reporting/employee-reports/${reportId}/`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Update an employee incident report
export const updateEmployeeIncidentReportApi = async (reportId, data) => {
    try {
        const response = await apiConfig.put(`/api/incident-reporting/employee-reports/${reportId}/`, data);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Delete an employee incident report
export const deleteEmployeeIncidentReportApi = async (reportId) => {
    try {
        const response = await apiConfig.delete(`/api/incident-reporting/employee-reports/${reportId}/`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Get incident categories
export const getIncidentCategoriesApi = async () => {
    try {
        const response = await apiConfig.get(`/api/incident-reporting/categories/`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Get incident severities
export const getIncidentSeveritiesApi = async () => {
    try {
        const response = await apiConfig.get(`/api/incident-reporting/severities/`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Get analytics metrics
export const getIncidentAnalyticsMetricsApi = async () => {
    try {
        const response = await apiConfig.get(`/api/incident-reporting/analytics/metrics`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Get SLA breaches
export const getIncidentSLABreachesApi = async () => {
    try {
        const response = await apiConfig.get(`/api/incident-reporting/analytics/sla-breaches`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Get AI severity suggestion
export const getAISeveritySuggestionApi = async (data) => {
    try {
        const response = await apiConfig.post(`/api/incident-reporting/ai-suggest-severity`, data);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Get logs for an incident report
export const getIncidentReportLogsApi = async (reportId) => {
    try {
        const response = await apiConfig.get(`/api/incident-reporting/employee-reports/${reportId}/logs/`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Create a log entry for an incident report
export const createIncidentReportLogApi = async (reportId, data) => {
    try {
        const response = await apiConfig.post(`/api/incident-reporting/employee-reports/${reportId}/logs/`, data);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Update incident status and create log entry atomically
export const updateIncidentStatusApi = async (reportId, data) => {
    try {
        const response = await apiConfig.put(`/api/incident-reporting/employee-reports/${reportId}/update-status/`, data);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// ========================================
// Emergency Contacts APIs
// ========================================

// Get emergency contacts for a customer
export const getEmergencyContactsApi = async (customerId) => {
    try {
        const response = await apiConfig.get(`/api/incident-reporting/emergency-contacts/?customer_id=${encodeURIComponent(customerId)}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Create a new emergency contact
export const createEmergencyContactApi = async (data) => {
    try {
        const response = await apiConfig.post(`/api/incident-reporting/emergency-contacts/`, data);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Update an emergency contact
export const updateEmergencyContactApi = async (contactId, data) => {
    try {
        const response = await apiConfig.put(`/api/incident-reporting/emergency-contacts/${contactId}/`, data);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Delete an emergency contact
export const deleteEmergencyContactApi = async (contactId, performedBy) => {
    try {
        const response = await apiConfig.delete(`/api/incident-reporting/emergency-contacts/${contactId}/?performed_by=${encodeURIComponent(performedBy)}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Get logs for an emergency contact
export const getEmergencyContactLogsApi = async (contactId) => {
    try {
        const response = await apiConfig.get(`/api/incident-reporting/emergency-contacts/${contactId}/logs/`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Get company employees list (for dropdown)
export const getCompanyEmployeesApi = async (customerId) => {
    try {
        const response = await apiConfig.get(`/api/incident-reporting/company-employees/?customer_id=${encodeURIComponent(customerId)}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};